import { ref, computed, onUnmounted } from 'vue';
import { api } from '@/api.js';
import { config } from '@/config.js';
import { useAbonoTracking } from '@/composables/front/analytics/useAbonoTracking';

/**
 * useInstantCredit
 * Composable for PAYCOMET Instant Credit financed payment (abono-only).
 *
 * State machine: cuotas → initiating → pending → final (paid|failed|cancelled|refunded)
 *
 * OQ-1 assumption: initiation response shape is `res.data.response.challengeUrl` for
 *   challenge redirect, and `res.data.response.order` (or `res.data.order`) for the
 *   polling order id. Status response returns `{ state, tpv_order }`.
 *   If backend contract differs, adjust `extractOrderFromResponse` and `extractTpvOrder`.
 *
 * OQ-2 assumption: `gastosGestionUnitarios` is a numeric per-cuota fee passed in by the
 *   container. The container derives it from `datos_guardados[].descuento.price_management`
 *   (or `price_management_interno`). The composable is decoupled from cart shape.
 *
 * @param {Object} deps
 * @param {String} deps.sessionId - Purchase session ID
 * @param {String} deps.tipoVenta - Sale type (always 'abono' for now)
 * @param {Number|Ref<Number>} deps.importeCarrito - Cart base price (without fees)
 * @param {Number|Ref<Number>} deps.gastosGestionUnitarios - Per-instalment management fee (per abonado)
 * @param {Number} [deps.numAbonados=1] - Number of abonados in cart. The fee is
 *   multiplied by this count so the UI total matches what will be charged per abonado.
 * @param {Object} deps.store - Vuex store (for getTeatro getter)
 * @param {Function} deps.getVuexAlias - Vuex alias resolver
 */

const FINAL_STATES = ['paid', 'failed', 'cancelled', 'refunded'];
const POLL_INTERVAL_MS = 4000;
const MAX_NETWORK_RETRIES = 3;
const CUOTAS_OPTIONS = [3, 6, 9];

export function useInstantCredit(deps = {}) {
    const {
        sessionId,
        tipoVenta = 'abono',
        importeCarrito,
        gastosGestionUnitarios,
        numAbonados = 1,
        store,
        getVuexAlias,
    } = deps;

    // ─── Telemetry ──────────────────────────────────────────────────────────
    const tracking = useAbonoTracking({ store, getVuexAlias });

    // ─── Reactive state ─────────────────────────────────────────────────────
    const nCuotas = ref(3);
    const phase = ref('cuotas'); // 'cuotas' | 'initiating' | 'pending' | 'final'
    const finalState = ref(null); // null | 'paid' | 'failed' | 'cancelled' | 'refunded'
    const initiating = ref(false);
    const polling = ref(false);
    const error = ref(null);

    // ─── Polling internals (non-reactive) ────────────────────────────────────
    let pollTimer = null;
    let pollController = null;
    let networkRetries = 0;
    let pollStopped = false;
    let currentOrder = null;
    let currentTpvOrder = null;

    // ─── Eligibility ────────────────────────────────────────────────────────
    const teatro = computed(() => {
        if (!store) return null;
        return store.getters.getTeatro || null;
    });

    const eligible = computed(() => {
        const t = teatro.value;
        if (!t) return false;
        if (!t.instant_credit_activo) return false;
        const minimo = parseFloat(t.instant_credit_importe_minimo || 0);
        const carrito = parseFloat(typeof importeCarrito === 'object' ? importeCarrito.value : importeCarrito) || 0;
        return carrito >= minimo && minimo > 0;
    });

    // ─── Breakdown ──────────────────────────────────────────────────────────
    const feePerCuota = computed(() => {
        const fee = parseFloat(typeof gastosGestionUnitarios === 'object' ? gastosGestionUnitarios.value : gastosGestionUnitarios) || 0;
        return fee;
    });

    const feeSubtotal = computed(() => feePerCuota.value * nCuotas.value * numAbonados);

    const importeTotal = computed(() => {
        const carrito = parseFloat(typeof importeCarrito === 'object' ? importeCarrito.value : importeCarrito) || 0;
        return carrito + feeSubtotal.value;
    });

    const breakdown = computed(() => ({
        base: parseFloat(typeof importeCarrito === 'object' ? importeCarrito.value : importeCarrito) || 0,
        feePerCuota: feePerCuota.value,
        feeSubtotal: feeSubtotal.value,
        total: importeTotal.value,
    }));

    // ─── Cuota selection ────────────────────────────────────────────────────
    const selectCuota = n => {
        if (!CUOTAS_OPTIONS.includes(n)) return;
        nCuotas.value = n;
        tracking.trackIcCuotasSelected(n, importeTotal.value);
    };

    // ─── Initiation ─────────────────────────────────────────────────────────
    const extractOrderFromResponse = res => {
        // OQ-1: backend may return order id in different locations.
        // Try common patterns: res.data.response.order, res.data.order, res.data.response.tpv_order
        const data = res?.data;
        if (!data) return null;
        return data.response?.order
            || data.response?.tpv_order
            || data.order
            || data.tpv_order
            || null;
    };

    const extractTpvOrder = data => {
        // OQ-1: tpv_order for the /compra redirect may come from initiation or status response
        if (!data) return null;
        return data.tpv_order
            || data.response?.tpv_order
            || data.order
            || data.response?.order
            || null;
    };

    const initiatePayment = async() => {
        if (initiating.value || phase.value !== 'cuotas') return;

        initiating.value = true;
        phase.value = 'initiating';
        error.value = null;

        try {
            const route = config.getAPIRoute('front.procesar-pago-paycomet');
            const url = route.url
                .replace('%tipo_venta%', tipoVenta)
                .replace('%session_id%', sessionId);

            const payload = {
                filters: {},
                session_id: sessionId,
                tipo_venta: tipoVenta,
                metodo_pago: 24,
                instant_credit: {
                    instalments: nCuotas.value,
                },
                financialProductId: null,
            };

            const res = await api.base().post(url, payload);

            tracking.trackIcInitiationSuccess();

            const challengeUrl = res?.data?.response?.challengeUrl || res?.data?.challengeUrl;

            if (challengeUrl) {
                // Challenge redirect — full browser navigation
                tracking.trackIcChallengeRedirected();
                window.location.href = challengeUrl;
                return;
            }

            // No challenge URL — start polling for status
            const order = extractOrderFromResponse(res);
            if (order) {
                currentOrder = order;
                currentTpvOrder = extractTpvOrder(res?.data);
                phase.value = 'pending';
                initiating.value = false;
                startPollingForOrder(order);
            } else {
                // No order id — unexpected response
                error.value = 'No order id in response';
                phase.value = 'cuotas';
                initiating.value = false;
            }
        } catch (err) {
            const errorCode = err?.response?.status || err?.code || 'unknown';
            tracking.trackIcInitiationFailed(errorCode);
            error.value = err?.response?.data?.message || err?.message || 'Payment initiation failed';
            phase.value = 'cuotas';
            initiating.value = false;
        }
    };

    // ─── Polling ────────────────────────────────────────────────────────────
    const fetchStatus = async order => {
        const route = config.getAPIRoute('front.get-instant-credit-status');
        const url = route.url.replace('%order%', order);
        pollController = new AbortController();
        return api.base().get(url, { signal: pollController.signal });
    };

    const handleFinalState = (state, data) => {
        pollStopped = true;
        polling.value = false;
        phase.value = 'final';
        finalState.value = state;
        currentTpvOrder = extractTpvOrder(data) || currentTpvOrder;

        tracking.trackIcPollFinalState(state);

        if (state === 'paid') {
            // Redirect to compra finalizada
            const sid = sessionId || '';
            const tpv = currentTpvOrder || '';
            window.location.href = `/compra/${sid}/${tpv}?error=false`;
        }
    };

    const poll = async order => {
        if (pollStopped) return;

        try {
            const res = await fetchStatus(order);
            networkRetries = 0;

            const state = res?.data?.state;
            if (FINAL_STATES.includes(state)) {
                handleFinalState(state, res.data);
                return;
            }

            // Non-final state — schedule next poll
            pollTimer = setTimeout(() => poll(order), POLL_INTERVAL_MS);
        } catch (err) {
            if (pollStopped || err?.name === 'AbortError') return;

            // 404 — order not found, stop immediately
            if (err?.response?.status === 404) {
                error.value = 'not_found';
                pollStopped = true;
                polling.value = false;
                phase.value = 'final';
                finalState.value = 'not_found';
                return;
            }

            // Network error — retry up to MAX_NETWORK_RETRIES
            if (++networkRetries > MAX_NETWORK_RETRIES) {
                error.value = 'network';
                pollStopped = true;
                polling.value = false;
                phase.value = 'final';
                finalState.value = 'failed';
                return;
            }

            // Schedule retry
            pollTimer = setTimeout(() => poll(order), POLL_INTERVAL_MS);
        }
    };

    const startPollingForOrder = order => {
        if (!order) return;
        pollStopped = false;
        networkRetries = 0;
        polling.value = true;
        phase.value = 'pending';
        currentOrder = order;
        poll(order);
    };

    const stopPolling = () => {
        pollStopped = true;
        clearTimeout(pollTimer);
        pollTimer = null;
        if (pollController) {
            pollController.abort();
            pollController = null;
        }
        polling.value = false;
    };

    // ─── Retry ──────────────────────────────────────────────────────────────
    const retryPayment = () => {
        stopPolling();
        phase.value = 'cuotas';
        finalState.value = null;
        error.value = null;
        currentOrder = null;
        currentTpvOrder = null;
    };

    // restartPayment — atomic retry-then-reinitiate.
    // Per WU-2 design D2: keep `retryPayment` as idempotent reset (no network) so the
    // existing 3 unit tests stay green; `restartPayment` chains `retryPayment` (which
    // aborts the prior `pollController`) with `initiatePayment` so one click yields one
    // new POST + new `tpv_order`. `initiating.value` guards against concurrent calls
    // from rapid retry clicks (per WU-2 risks).
    const restartPayment = async() => {
        retryPayment();
        return initiatePayment();
    };

    // ─── Cleanup ────────────────────────────────────────────────────────────
    onUnmounted(() => {
        stopPolling();
    });

    return {
        eligible,
        cuotasOptions: CUOTAS_OPTIONS,
        nCuotas,
        breakdown,
        importeTotal,
        phase,
        finalState,
        initiating,
        polling,
        error,
        selectCuota,
        initiatePayment,
        retryPayment,
        restartPayment,
        startPollingForOrder,
        stopPolling,
    };
}
